import React, {
  useEffect,
  useReducer,
  Dispatch,
  createContext,
  useMemo,
} from 'react';
import NextNProgress from 'nextjs-progressbar';
import Head from 'next/head';
import reducer from 'stores/reducer';
import initialStore, { initialStoreType } from 'stores';
import { useRouter } from 'next/router';
import { setSideMenu } from 'stores/actions';
import { ToastContainer } from 'react-toastify';
import { appWithTranslation } from 'next-i18next';
import { NextPageWithLayout } from 'next';
import { AppProps } from 'next/app';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Inter } from 'next/font/google';

import 'styles/global.css';
import 'remixicon/fonts/remixicon.css';
import 'flag-icon-css/css/flag-icon.min.css';
import 'react-toastify/dist/ReactToastify.css';
import 'react-quill/dist/quill.snow.css';
import 'styles/components/RichTextEditor.css';
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';

const interFont = Inter({
  subsets: ['latin'],
  display: 'swap',
  variable: '--font-inter',
  weight: ['300', '400', '500', '600', '700'],
});

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error) {
          return false;
        }
        if (failureCount < 3) {
          return true;
        }

        return false;
      },
      refetchOnWindowFocus: false,
    },
  },
});

export const AppContext = createContext<{
  state: initialStoreType;
  dispatch: Dispatch<Function | { type: string; value: any }>;
}>({
  state: initialStore,
  dispatch: () => void 0,
});

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const [state, dispatch] = useReducer(reducer, initialStore);
  const Router = useRouter();

  useEffect(() => {
    Router.events.on('routeChangeStart', () => {
      dispatch(setSideMenu(false));
    });
  }, []);

  const getLayout = Component.getLayout || ((page) => page);

  const finalComponent = useMemo(() => {
    return getLayout(<Component {...pageProps} />, pageProps);
  }, [Component, pageProps]);

  function renderDebugButton() {
    if (process.env.NEXT_PUBLIC_DEBUG !== 'true') return null;

    return (
      <a
        onClick={() => {
          throw new Error('Test Error Sentry');
        }}
        className="text-white font-bold text-title1 fixed left-16 ipad:left-48 bottom-16 ipad:bottom-48 w-48 h-48 flex items-center justify-center z-30 bg-gradient-to-bl from-primary to-info-light rounded-lg shadow-lg border border-grey-lightest"
      >
        <i className="ri-terminal-line" />
      </a>
    );
  }

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      <Head>
        <link rel="shortcut icon" type="image/jpeg" href="/images/logo2.png" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Head>
      <NextNProgress
        height={3}
        color='#CEFE62'
        options={{ showSpinner: false }}
      />
      <QueryClientProvider client={queryClient}>
        <main className={interFont.className}>
          {finalComponent}
        </main>
        {renderDebugButton()}
      </QueryClientProvider>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="light"
      />
    </AppContext.Provider>
  );
}

export default appWithTranslation(MyApp);
